import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, FileText, User, Edit2, Save, X, Phone, MapPin, Building2, CreditCard, Shield, Calendar, Hash, Briefcase, Globe, Key } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';


const PROFILE_FIELDS = [
  { key: 'worker_code', label: 'Codigo Trabajador', icon: Hash },
  { key: 'dni', label: 'DNI / NIE', icon: Shield },
  { key: 'social_security', label: 'Seguridad Social', icon: CreditCard },
  { key: 'bank_account', label: 'Cuenta Bancaria (IBAN)', icon: CreditCard },
  { key: 'phone', label: 'Telefono', icon: Phone },
  { key: 'birth_date', label: 'Fecha Nacimiento', icon: Calendar, type: 'date' },
  { key: 'address', label: 'Direccion / Residencia', icon: MapPin },
  { key: 'work_center', label: 'Centro de Trabajo', icon: Building2 },
  { key: 'position', label: 'Puesto', icon: Briefcase },
  { key: 'start_date', label: 'Antiguedad (fecha inicio)', icon: Calendar, type: 'date' },
  { key: 'nationality', label: 'Nacionalidad', icon: Globe },
  { key: 'emergency_contact', label: 'Contacto Emergencia', icon: Phone },
];

const getDocTypeBadgeColor = (type) => {
  switch (type) {
    case 'DNI': return 'bg-blue-100 text-blue-800';
    case 'TIP Seguridad Privada': return 'bg-amber-100 text-amber-800';
    case 'Seguridad Social': return 'bg-emerald-100 text-emerald-800';
    case 'Certificado Cuenta Bancaria': return 'bg-purple-100 text-purple-800';
    default: return 'bg-orange-100 text-orange-800';
  }
};

export default function WorkerDetailPage() {
  const { workerId } = useParams();
  const navigate = useNavigate();
  const [worker, setWorker] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [workerId]);

  const fetchData = async () => {
    try {
      const [workerRes, docsRes] = await Promise.all([
        API.get(`/workers/${workerId}`),
        API.get(`/documents/worker/${workerId}`)
      ]);
      setWorker(workerRes.data);
      setEditData(workerRes.data);
      setDocuments(docsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {};
      PROFILE_FIELDS.forEach(f => {
        if (editData[f.key] !== worker[f.key]) {
          updates[f.key] = editData[f.key] || '';
        }
      });
      if (editData.name !== worker.name) updates.name = editData.name;
      
      if (Object.keys(updates).length > 0) {
        await API.put(`/workers/${workerId}`, updates);
        setWorker({ ...worker, ...updates });
      }
      setEditing(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) return;
    setPasswordSaving(true);
    try {
      await API.put(`/workers/${workerId}/password`, { password: newPassword });
      setPasswordDialog(false);
      setNewPassword('');
    } catch (err) {
      console.error(err);
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await API.get(`/documents/download/${doc.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!worker) {
    return <div className="text-center py-12 text-gray-500">Trabajador no encontrado</div>;
  }

  return (
    <div className="space-y-6" data-testid="worker-detail-page">
      {/* Back */}
      <button onClick={() => navigate('/company/workers')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#FF6B35] transition-colors">
        <ArrowLeft className="w-4 h-4" />
        Volver a trabajadores
      </button>

      {/* Header Card */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center shadow-md shadow-orange-200">
              <User className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{worker.name}</h1>
              <p className="text-sm text-gray-500">{worker.email}</p>
              <div className="flex items-center gap-2 mt-1">
                {worker.worker_code && (
                  <Badge className="bg-orange-50 text-[#FF6B35] text-xs">#{worker.worker_code}</Badge>
                )}
                {worker.position && (
                  <Badge className="bg-blue-50 text-blue-600 text-xs">{worker.position}</Badge>
                )}
                <span className="text-xs text-gray-400">
                  Registrado el {new Date(worker.created_at).toLocaleDateString('es-ES')}
                </span>
              </div>
            </div>
          </div>
          <div>
            {editing ? (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => { setEditing(false); setEditData(worker); }} disabled={saving}>
                  <X className="w-4 h-4 mr-1" /> Cancelar
                </Button>
                <Button size="sm" className="bg-[#FF6B35] hover:bg-[#E55A2B] text-white" onClick={handleSave} disabled={saving} data-testid="save-worker-btn">
                  <Save className="w-4 h-4 mr-1" /> {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-gray-200 text-gray-600 hover:bg-gray-50" onClick={() => setPasswordDialog(true)} data-testid="change-password-btn">
                  <Key className="w-4 h-4 mr-1" /> Contraseña
                </Button>
                <Button size="sm" variant="outline" className="border-orange-200 text-[#FF6B35] hover:bg-orange-50" onClick={() => setEditing(true)} data-testid="edit-worker-btn">
                  <Edit2 className="w-4 h-4 mr-1" /> Editar
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Fields Grid */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Datos del trabajador</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PROFILE_FIELDS.map(field => {
            const Icon = field.icon;
            return (
              <div key={field.key} className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5" />
                  {field.label}
                </Label>
                {editing ? (
                  <Input
                    type={field.type || 'text'}
                    value={editData[field.key] || ''}
                    onChange={(e) => setEditData({ ...editData, [field.key]: e.target.value })}
                    className="h-9 text-sm border-gray-200 focus:ring-[#FF6B35]"
                    data-testid={`field-${field.key}`}
                  />
                ) : (
                  <p className="text-sm text-gray-900 py-1.5 px-0 min-h-[36px]">
                    {worker[field.key] || <span className="text-gray-300 italic">Sin datos</span>}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Documents */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="p-5 border-b border-gray-100">
          <h2 className="text-lg font-medium text-gray-900">Documentos</h2>
          <p className="text-xs text-gray-500">{documents.length} documento(s) subido(s)</p>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-gray-100">
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Tipo</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Archivo</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Fecha</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Estado</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-400 text-sm">
                    Este trabajador no tiene documentos subidos
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((doc) => (
                  <TableRow key={doc.id} className="hover:bg-gray-50/50">
                    <TableCell>
                      <Badge className={getDocTypeBadgeColor(doc.document_type)}>{doc.document_type}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-700">{doc.original_filename}</TableCell>
                    <TableCell className="text-xs text-gray-500">{new Date(doc.created_at).toLocaleDateString('es-ES')}</TableCell>
                    <TableCell>
                      <Badge className={
                        doc.validation_status === 'approved' ? 'bg-green-50 text-green-700' :
                        doc.validation_status === 'rejected' ? 'bg-red-50 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }>
                        {doc.validation_status === 'approved' ? 'Aprobado' :
                         doc.validation_status === 'rejected' ? 'Rechazado' :
                         doc.validation_status === 'reviewed' ? 'Revisado' : 'Pendiente'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="text-gray-500 hover:text-[#FF6B35]" onClick={() => handleDownload(doc)}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Password Dialog */}
      <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Cambiar Contraseña</DialogTitle>
            <DialogDescription>Nueva contraseña para {worker?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-600">Nueva contraseña</Label>
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min. 6 caracteres"
                data-testid="new-password-input"
              />
            </div>
            <Button
              onClick={handleChangePassword}
              disabled={passwordSaving || newPassword.length < 6}
              className="w-full bg-[#FF6B35] hover:bg-[#E55A2B] text-white"
              data-testid="save-password-btn"
            >
              {passwordSaving ? 'Guardando...' : 'Cambiar Contraseña'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
